import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
  BookOpen, Library, Users, AlertCircle, Plus, Search, ArrowRightLeft,
  FileText, Download, RefreshCw, DollarSign, Barcode, Clock
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Student Library View Component
const StudentLibraryView = ({ getAuthHeader }) => {
  const [books, setBooks] = useState([]);
  const [myIssues, setMyIssues] = useState([]);
  const [eResources, setEResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [booksRes, eLibRes, myIssuedRes] = await Promise.all([
        axios.get(`${API_URL}/api/library/v2/books`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/library/v2/e-library`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/library/v2/my-issued`, { headers: getAuthHeader() })
      ]);
      // Handle paginated responses
      setBooks(booksRes.data.items || booksRes.data || []);
      setEResources(eLibRes.data.items || eLibRes.data || []);
      // Get student's own issued books
      setMyIssues(myIssuedRes.data?.issued || []);
    } catch (error) {
      console.error('Failed to fetch library data:', error);
      toast.error('Failed to load library data');
    } finally {
      setLoading(false);
    }
  };

  const filteredBooks = books.filter(book => 
    book.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    book.author?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    book.isbn?.includes(searchQuery)
  );

  if (loading) {
    return (
      <DashboardLayout title="My Library">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Library">
      <div className="space-y-6">
        {/* My Borrowed Books */}
        {myIssues.length > 0 && (
          <Card className="border-amber-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                My Borrowed Books
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {myIssues.map((issue, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{issue.book_title}</p>
                      <p className="text-sm text-muted-foreground">Due: {issue.due_date}</p>
                    </div>
                    <Badge variant={new Date(issue.due_date) < new Date() ? 'destructive' : 'outline'}>
                      {new Date(issue.due_date) < new Date() ? 'Overdue' : 'Borrowed'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search Books */}
        <Card>
          <CardHeader>
            <CardTitle>Browse Library</CardTitle>
            <CardDescription>Search and explore available books</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by title, author, or ISBN..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {filteredBooks.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No books found</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredBooks.slice(0, 12).map((book, idx) => (
                  <div key={idx} className="p-4 border rounded-lg">
                    <h3 className="font-semibold">{book.title}</h3>
                    <p className="text-sm text-muted-foreground">{book.author}</p>
                    <div className="flex justify-between items-center mt-2">
                      <Badge variant="outline">{book.category_name || 'General'}</Badge>
                      <span className={`text-sm ${(book.available || book.available_copies || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {(book.available || book.available_copies || 0) > 0 ? `${book.available || book.available_copies} available` : 'Not available'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* E-Library Resources */}
        {eResources.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                E-Library Resources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {eResources.map((resource, idx) => (
                  <div key={idx} className="p-4 border rounded-lg flex justify-between items-center">
                    <div>
                      <p className="font-medium">{resource.title}</p>
                      <p className="text-sm text-muted-foreground">{resource.resource_type}</p>
                    </div>
                    {resource.url && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={resource.url} target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4 mr-1" /> Access
                        </a>
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

const LibraryPageV2Admin = () => {
  const { getAuthHeader, user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({});
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [issues, setIssues] = useState([]);
  const [overdueIssues, setOverdueIssues] = useState([]);
  const [eResources, setEResources] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [settings, setSettings] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state for books
  const [booksPage, setBooksPage] = useState(1);
  const [booksPagination, setBooksPagination] = useState(null);
  const pageSize = 50;
  
  // Dialogs
  const [bookDialog, setBookDialog] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [issueDialog, setIssueDialog] = useState(false);
  const [returnDialog, setReturnDialog] = useState(false);
  const [eResourceDialog, setEResourceDialog] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  
  // Forms
  const [bookForm, setBookForm] = useState({
    title: '', author: '', isbn: '', category_id: '', publisher: '', publication_year: new Date().getFullYear(),
    language: 'English', quantity: 1, price: 0, rack_number: '', shelf_number: ''
  });
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [issueForm, setIssueForm] = useState({
    book_id: '', member_type: 'student', member_id: ''
  });
  const [eResourceForm, setEResourceForm] = useState({
    title: '', resource_type: 'pdf', author: '', category_id: '', description: '', file_url: '', access_type: 'all'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, booksRes, catRes, issueRes, overdueRes, eResRes, settRes, studRes, teachRes] = await Promise.all([
        axios.get(`${API_URL}/api/library/v2/dashboard`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/library/v2/books?page=${booksPage}&page_size=${pageSize}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/library/v2/categories`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/library/v2/issues?status=issued`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/library/v2/issues?overdue_only=true`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/library/v2/e-library`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/library/v2/settings`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() })
      ]);
      setDashboard(dashRes.data);
      // Handle paginated responses - extract items array
      if (booksRes.data.items) {
        setBooks(booksRes.data.items);
        setBooksPagination({
          page: booksRes.data.page,
          total: booksRes.data.total,
          total_pages: booksRes.data.total_pages,
          has_next: booksRes.data.has_next,
          has_prev: booksRes.data.has_prev
        });
      } else {
        setBooks(booksRes.data);
        setBooksPagination(null);
      }
      setCategories(catRes.data);
      setIssues(issueRes.data.items || issueRes.data);
      setOverdueIssues(overdueRes.data.items || overdueRes.data);
      setEResources(eResRes.data.items || eResRes.data);
      setSettings(settRes.data);
      setStudents(studRes.data.items || studRes.data);
      setTeachers(teachRes.data.items || teachRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch books with pagination
  const fetchBooks = async (page = 1) => {
    try {
      const res = await axios.get(`${API_URL}/api/library/v2/books?page=${page}&page_size=${pageSize}`, { headers: getAuthHeader() });
      if (res.data.items) {
        setBooks(res.data.items);
        setBooksPagination({
          page: res.data.page,
          total: res.data.total,
          total_pages: res.data.total_pages,
          has_next: res.data.has_next,
          has_prev: res.data.has_prev
        });
      } else {
        setBooks(res.data);
        setBooksPagination(null);
      }
      setBooksPage(page);
    } catch (error) {
      toast.error('Failed to fetch books');
    }
  };

  const addBook = async () => {
    try {
      await axios.post(`${API_URL}/api/library/v2/books`, null, {
        headers: getAuthHeader(),
        params: bookForm
      });
      toast.success('Book added');
      setBookDialog(false);
      setBookForm({ title: '', author: '', isbn: '', category_id: '', publisher: '', publication_year: new Date().getFullYear(),
        language: 'English', quantity: 1, price: 0, rack_number: '', shelf_number: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add book');
    }
  };

  const addCategory = async () => {
    try {
      await axios.post(`${API_URL}/api/library/v2/categories`, null, {
        headers: getAuthHeader(),
        params: categoryForm
      });
      toast.success('Category added');
      setCategoryDialog(false);
      setCategoryForm({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const issueBook = async () => {
    try {
      await axios.post(`${API_URL}/api/library/v2/issue`, null, {
        headers: getAuthHeader(),
        params: issueForm
      });
      toast.success('Book issued');
      setIssueDialog(false);
      setIssueForm({ book_id: '', member_type: 'student', member_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue book');
    }
  };

  const returnBook = async (issueId, waiveFine = false) => {
    try {
      await axios.post(`${API_URL}/api/library/v2/return/${issueId}`, null, {
        headers: getAuthHeader(),
        params: { waive_fine: waiveFine }
      });
      toast.success('Book returned');
      setReturnDialog(false);
      setSelectedIssue(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to return book');
    }
  };

  const renewBook = async (issueId) => {
    try {
      await axios.post(`${API_URL}/api/library/v2/renew/${issueId}`, null, { headers: getAuthHeader() });
      toast.success('Book renewed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to renew');
    }
  };

  const addEResource = async () => {
    try {
      await axios.post(`${API_URL}/api/library/v2/e-library`, null, {
        headers: getAuthHeader(),
        params: eResourceForm
      });
      toast.success('E-Resource added');
      setEResourceDialog(false);
      setEResourceForm({ title: '', resource_type: 'pdf', author: '', category_id: '', description: '', file_url: '', access_type: 'all' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add resource');
    }
  };

  const searchByBarcode = async () => {
    if (!barcodeInput) return;
    try {
      const res = await axios.get(`${API_URL}/api/library/v2/books/barcode/${barcodeInput}`, { headers: getAuthHeader() });
      setIssueForm({ ...issueForm, book_id: res.data.id });
      toast.success(`Found: ${res.data.title}`);
      setBarcodeInput('');
    } catch (error) {
      toast.error('Book not found');
    }
  };

  const filteredBooks = books.filter(b => 
    b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (b.isbn && b.isbn.includes(searchQuery)) ||
    (b.barcode && b.barcode.includes(searchQuery))
  );

  const stats = [
    { title: 'Total Titles', value: dashboard.total_titles || 0, icon: BookOpen, color: 'bg-blue-500' },
    { title: 'Total Copies', value: dashboard.total_copies || 0, icon: Library, color: 'bg-purple-500' },
    { title: 'Available', value: dashboard.available_copies || 0, icon: BookOpen, color: 'bg-green-500' },
    { title: 'Issued', value: dashboard.books_issued || 0, icon: ArrowRightLeft, color: 'bg-amber-500' },
    { title: 'Overdue', value: dashboard.overdue_books || 0, icon: AlertCircle, color: 'bg-red-500' },
    { title: 'E-Resources', value: dashboard.total_e_resources || 0, icon: FileText, color: 'bg-indigo-500' },
  ];

  return (
    <DashboardLayout title="Library Management">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="books">Books</TabsTrigger>
          <TabsTrigger value="issue">Issue/Return</TabsTrigger>
          <TabsTrigger value="overdue">Overdue</TabsTrigger>
          <TabsTrigger value="e-library">E-Library</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-xs font-medium text-muted-foreground">{stat.title}</CardTitle>
                      <div className={`${stat.color} p-2 rounded-lg`}><Icon className="h-3.5 w-3.5 text-white" /></div>
                    </CardHeader>
                    <CardContent><div className="text-2xl font-bold">{stat.value}</div></CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Today's Activity</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Books Issued:</span><span className="font-bold">{dashboard.today_issues || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Books Returned:</span><span className="font-bold">{dashboard.today_returns || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Pending Fines:</span><span className="font-bold text-red-600">₹{dashboard.pending_fines || 0}</span></div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader><CardTitle className="text-base">Quick Actions</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <Button className="w-full justify-start" variant="outline" onClick={() => setBookDialog(true)}><BookOpen className="h-4 w-4 mr-2" />Add Book</Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => setIssueDialog(true)}><ArrowRightLeft className="h-4 w-4 mr-2" />Issue Book</Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => setEResourceDialog(true)}><FileText className="h-4 w-4 mr-2" />Add E-Resource</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Books */}
        <TabsContent value="books" className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by title, author, ISBN, barcode..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-books" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCategoryDialog(true)}><Plus className="h-4 w-4 mr-2" />Category</Button>
              <Button onClick={() => setBookDialog(true)} data-testid="add-book-btn"><Plus className="h-4 w-4 mr-2" />Add Book</Button>
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium">Title</th>
                    <th className="p-3 text-left text-sm font-medium">Author</th>
                    <th className="p-3 text-left text-sm font-medium">ISBN/Barcode</th>
                    <th className="p-3 text-left text-sm font-medium">Category</th>
                    <th className="p-3 text-left text-sm font-medium">Available</th>
                    <th className="p-3 text-left text-sm font-medium">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBooks.map(book => (
                    <tr key={book.id} className="border-t" data-testid={`book-${book.barcode}`}>
                      <td className="p-3 font-medium">{book.title}</td>
                      <td className="p-3">{book.author}</td>
                      <td className="p-3 font-mono text-sm">{book.barcode || book.isbn}</td>
                      <td className="p-3">{book.category_name || '-'}</td>
                      <td className="p-3">
                        <Badge variant={book.available > 0 ? 'default' : 'destructive'}>{book.available}/{book.quantity}</Badge>
                      </td>
                      <td className="p-3 text-sm">{book.rack_number && `R${book.rack_number}`} {book.shelf_number && `S${book.shelf_number}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Books Pagination */}
              {booksPagination && booksPagination.total_pages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Page {booksPage} of {booksPagination.total_pages} ({booksPagination.total} total books)
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchBooks(booksPage - 1)}
                      disabled={!booksPagination.has_prev}
                      data-testid="books-prev-page"
                    >
                      Previous
                    </Button>
                    <span className="text-sm px-2">Page {booksPage}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchBooks(booksPage + 1)}
                      disabled={!booksPagination.has_next}
                      data-testid="books-next-page"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Issue/Return */}
        <TabsContent value="issue" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Currently Issued ({issues.length})</h3>
            <Button onClick={() => setIssueDialog(true)} data-testid="issue-book-btn"><ArrowRightLeft className="h-4 w-4 mr-2" />Issue Book</Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium">Book</th>
                    <th className="p-3 text-left text-sm font-medium">Issued To</th>
                    <th className="p-3 text-left text-sm font-medium">Type</th>
                    <th className="p-3 text-left text-sm font-medium">Issue Date</th>
                    <th className="p-3 text-left text-sm font-medium">Due Date</th>
                    <th className="p-3 text-left text-sm font-medium">Renewals</th>
                    <th className="p-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map(issue => (
                    <tr key={issue.id} className="border-t">
                      <td className="p-3 font-medium">{issue.book_title}</td>
                      <td className="p-3">{issue.member_name}</td>
                      <td className="p-3 capitalize">{issue.member_type}</td>
                      <td className="p-3">{issue.issued_date}</td>
                      <td className="p-3">
                        <span className={issue.due_date < new Date().toISOString().split('T')[0] ? 'text-red-600 font-bold' : ''}>{issue.due_date}</span>
                      </td>
                      <td className="p-3">{issue.renewals || 0}/{settings.max_renewals || 2}</td>
                      <td className="p-3 space-x-1">
                        <Button size="sm" variant="outline" onClick={() => renewBook(issue.id)} title="Renew"><RefreshCw className="h-3 w-3" /></Button>
                        <Button size="sm" onClick={() => { setSelectedIssue(issue); setReturnDialog(true); }}>Return</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Overdue */}
        <TabsContent value="overdue" className="space-y-4">
          <h3 className="text-lg font-semibold text-red-600">Overdue Books ({overdueIssues.length})</h3>
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-red-50">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium">Book</th>
                    <th className="p-3 text-left text-sm font-medium">Issued To</th>
                    <th className="p-3 text-left text-sm font-medium">Due Date</th>
                    <th className="p-3 text-left text-sm font-medium">Days Overdue</th>
                    <th className="p-3 text-left text-sm font-medium">Fine</th>
                    <th className="p-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {overdueIssues.map(issue => {
                    const daysOverdue = Math.floor((new Date() - new Date(issue.due_date)) / (1000 * 60 * 60 * 24));
                    const fine = Math.min(daysOverdue * (settings.fine_per_day || 2), settings.max_fine_amount || 100);
                    return (
                      <tr key={issue.id} className="border-t">
                        <td className="p-3 font-medium">{issue.book_title}</td>
                        <td className="p-3">{issue.member_name}</td>
                        <td className="p-3 text-red-600">{issue.due_date}</td>
                        <td className="p-3 font-bold text-red-600">{daysOverdue} days</td>
                        <td className="p-3 font-bold">₹{fine}</td>
                        <td className="p-3">
                          <Button size="sm" onClick={() => { setSelectedIssue({...issue, fine_amount: fine}); setReturnDialog(true); }}>Return</Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* E-Library */}
        <TabsContent value="e-library" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">E-Library / Digital Resources ({eResources.length})</h3>
            <Button onClick={() => setEResourceDialog(true)} data-testid="add-eresource-btn"><Plus className="h-4 w-4 mr-2" />Add Resource</Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {eResources.map(res => (
              <Card key={res.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-base">{res.title}</CardTitle>
                      {res.author && <CardDescription>{res.author}</CardDescription>}
                    </div>
                    <Badge variant="outline" className="capitalize">{res.resource_type}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {res.description && <p className="text-muted-foreground line-clamp-2">{res.description}</p>}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Views:</span><span>{res.views || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Access:</span><span className="capitalize">{res.access_type}</span>
                  </div>
                  {res.file_url && (
                    <Button size="sm" className="w-full" variant="outline" onClick={() => window.open(res.file_url, '_blank')}>
                      <Download className="h-3 w-3 mr-1" />Open/Download
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Library Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max Books per Student</Label>
                  <div className="text-2xl font-bold">{settings.max_books_per_student || 3}</div>
                </div>
                <div className="space-y-2">
                  <Label>Max Books per Teacher</Label>
                  <div className="text-2xl font-bold">{settings.max_books_per_teacher || 5}</div>
                </div>
                <div className="space-y-2">
                  <Label>Loan Period (Days)</Label>
                  <div className="text-2xl font-bold">{settings.loan_period_days || 14}</div>
                </div>
                <div className="space-y-2">
                  <Label>Fine per Day (₹)</Label>
                  <div className="text-2xl font-bold">₹{settings.fine_per_day || 2}</div>
                </div>
                <div className="space-y-2">
                  <Label>Max Fine (₹)</Label>
                  <div className="text-2xl font-bold">₹{settings.max_fine_amount || 100}</div>
                </div>
                <div className="space-y-2">
                  <Label>Max Renewals</Label>
                  <div className="text-2xl font-bold">{settings.max_renewals || 2}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Book Dialog */}
      <Dialog open={bookDialog} onOpenChange={setBookDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Book</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Title *</Label><Input value={bookForm.title} onChange={e => setBookForm({...bookForm, title: e.target.value})} /></div>
            <div><Label>Author *</Label><Input value={bookForm.author} onChange={e => setBookForm({...bookForm, author: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>ISBN</Label><Input value={bookForm.isbn} onChange={e => setBookForm({...bookForm, isbn: e.target.value})} /></div>
              <div><Label>Category</Label>
                <Select value={bookForm.category_id} onValueChange={v => setBookForm({...bookForm, category_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Publisher</Label><Input value={bookForm.publisher} onChange={e => setBookForm({...bookForm, publisher: e.target.value})} /></div>
              <div><Label>Year</Label><Input type="number" value={bookForm.publication_year} onChange={e => setBookForm({...bookForm, publication_year: parseInt(e.target.value)})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Quantity *</Label><Input type="number" value={bookForm.quantity} onChange={e => setBookForm({...bookForm, quantity: parseInt(e.target.value)})} min={1} /></div>
              <div><Label>Price (₹)</Label><Input type="number" value={bookForm.price} onChange={e => setBookForm({...bookForm, price: parseFloat(e.target.value)})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Rack No.</Label><Input value={bookForm.rack_number} onChange={e => setBookForm({...bookForm, rack_number: e.target.value})} /></div>
              <div><Label>Shelf No.</Label><Input value={bookForm.shelf_number} onChange={e => setBookForm({...bookForm, shelf_number: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={addBook}>Add Book</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Category Name *</Label><Input value={categoryForm.name} onChange={e => setCategoryForm({...categoryForm, name: e.target.value})} placeholder="e.g., Fiction, Science" /></div>
            <div><Label>Description</Label><Textarea value={categoryForm.description} onChange={e => setCategoryForm({...categoryForm, description: e.target.value})} /></div>
          </div>
          <DialogFooter><Button onClick={addCategory}>Add Category</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Book Dialog */}
      <Dialog open={issueDialog} onOpenChange={setIssueDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Issue Book</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Scan Barcode/ISBN</Label>
              <div className="flex gap-2">
                <Input value={barcodeInput} onChange={e => setBarcodeInput(e.target.value)} placeholder="Scan or enter barcode" onKeyDown={e => e.key === 'Enter' && searchByBarcode()} />
                <Button onClick={searchByBarcode}><Barcode className="h-4 w-4" /></Button>
              </div>
            </div>
            <div><Label>Or Select Book</Label>
              <Select value={issueForm.book_id} onValueChange={v => setIssueForm({...issueForm, book_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select book" /></SelectTrigger>
                <SelectContent>
                  {books.filter(b => b.available > 0).map(b => <SelectItem key={b.id} value={b.id}>{b.title} ({b.available} available)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Member Type</Label>
              <Select value={issueForm.member_type} onValueChange={v => setIssueForm({...issueForm, member_type: v, member_id: ''})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Select {issueForm.member_type === 'student' ? 'Student' : 'Teacher'}</Label>
              <Select value={issueForm.member_id} onValueChange={v => setIssueForm({...issueForm, member_id: v})}>
                <SelectTrigger><SelectValue placeholder={`Select ${issueForm.member_type}`} /></SelectTrigger>
                <SelectContent>
                  {(issueForm.member_type === 'student' ? students : teachers).map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name} ({m.student_id || m.employee_id})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={issueBook}>Issue Book</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Book Dialog */}
      <Dialog open={returnDialog} onOpenChange={setReturnDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Return Book</DialogTitle></DialogHeader>
          {selectedIssue && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p><strong>Book:</strong> {selectedIssue.book_title}</p>
                <p><strong>Issued To:</strong> {selectedIssue.member_name}</p>
                <p><strong>Due Date:</strong> {selectedIssue.due_date}</p>
                {selectedIssue.fine_amount > 0 && (
                  <p className="text-red-600 font-bold mt-2">Fine: ₹{selectedIssue.fine_amount}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => returnBook(selectedIssue.id, false)} className="flex-1">
                  Return {selectedIssue.fine_amount > 0 && `(Collect ₹${selectedIssue.fine_amount})`}
                </Button>
                {selectedIssue.fine_amount > 0 && (
                  <Button variant="outline" onClick={() => returnBook(selectedIssue.id, true)}>Waive Fine</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add E-Resource Dialog */}
      <Dialog open={eResourceDialog} onOpenChange={setEResourceDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add E-Resource</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={eResourceForm.title} onChange={e => setEResourceForm({...eResourceForm, title: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Type</Label>
                <Select value={eResourceForm.resource_type} onValueChange={v => setEResourceForm({...eResourceForm, resource_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="ebook">E-Book</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="audio">Audio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Access</Label>
                <Select value={eResourceForm.access_type} onValueChange={v => setEResourceForm({...eResourceForm, access_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="students">Students Only</SelectItem>
                    <SelectItem value="teachers">Teachers Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Author</Label><Input value={eResourceForm.author} onChange={e => setEResourceForm({...eResourceForm, author: e.target.value})} /></div>
            <div><Label>File URL *</Label><Input value={eResourceForm.file_url} onChange={e => setEResourceForm({...eResourceForm, file_url: e.target.value})} placeholder="https://..." /></div>
            <div><Label>Description</Label><Textarea value={eResourceForm.description} onChange={e => setEResourceForm({...eResourceForm, description: e.target.value})} /></div>
          </div>
          <DialogFooter><Button onClick={addEResource}>Add Resource</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// Main Component with Role-based Routing
const LibraryPageWrapper = () => {
  const { getAuthHeader, user } = useAuth();
  
  // Student gets simplified view
  if (user?.role === 'student') {
    return <StudentLibraryView getAuthHeader={getAuthHeader} />;
  }
  
  // Teacher gets Issue/Return only view (restricted)
  if (user?.role === 'teacher') {
    return <TeacherLibraryView />;
  }
  
  // Admin gets full view
  return <LibraryPageV2Admin />;
};

// Teacher Library View - Issue/Return Only
const TeacherLibraryView = () => {
  const { getAuthHeader } = useAuth();
  const [books, setBooks] = useState([]);
  const [issues, setIssues] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [booksRes, issuesRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/library/v2/books`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/library/v2/issues?status=issued`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() })
      ]);
      // Handle paginated response for books
      const booksData = booksRes.data?.items || booksRes.data || [];
      setBooks(Array.isArray(booksData) ? booksData : []);
      // Handle paginated response for issues
      const issuesData = issuesRes.data?.items || issuesRes.data || [];
      setIssues(Array.isArray(issuesData) ? issuesData : []);
      // Handle paginated response for students
      setStudents(studentsRes.data?.items || studentsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch library data:', error);
      toast.error('Failed to load library data');
    } finally {
      setLoading(false);
    }
  };

  const handleIssueBook = async () => {
    if (!selectedBook || !selectedStudent) {
      toast.error('Please select both book and student');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/library/v2/issue`, {
        book_id: selectedBook.id,
        student_id: selectedStudent,
        due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }, { headers: getAuthHeader() });
      toast.success('Book issued successfully');
      setIssueDialogOpen(false);
      setSelectedBook(null);
      setSelectedStudent('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue book');
    }
  };

  const handleReturnBook = async (issueId) => {
    try {
      await axios.post(`${API_URL}/api/library/v2/return/${issueId}`, {}, { headers: getAuthHeader() });
      toast.success('Book returned successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return book');
    }
  };

  const filteredBooks = books.filter(book => 
    book.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    book.author?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    book.isbn?.includes(searchQuery)
  );

  if (loading) {
    return (
      <DashboardLayout title="Library (Issue/Return)">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Library (Issue/Return)">
      <div className="space-y-6">
        {/* Info Banner */}
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">You can issue and return books. Contact admin to add new books or manage inventory.</span>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Books</p>
                  <p className="text-2xl font-bold">{books.length}</p>
                </div>
                <BookOpen className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Currently Issued</p>
                  <p className="text-2xl font-bold text-orange-600">{issues.length}</p>
                </div>
                <ArrowRightLeft className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">My Students</p>
                  <p className="text-2xl font-bold text-green-600">{students.length}</p>
                </div>
                <Users className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="issue">
          <TabsList>
            <TabsTrigger value="issue">Issue Book</TabsTrigger>
            <TabsTrigger value="return">Return Book ({issues.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="issue">
            <Card>
              <CardHeader>
                <CardTitle>Available Books</CardTitle>
                <CardDescription>Select a book to issue to a student</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by title, author, or ISBN..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                {filteredBooks.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No books found</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredBooks.filter(b => b.available_copies > 0).slice(0, 12).map((book, idx) => (
                      <div key={idx} className="p-4 border rounded-lg hover:border-primary cursor-pointer transition-colors"
                           onClick={() => { setSelectedBook(book); setIssueDialogOpen(true); }}>
                        <h3 className="font-semibold">{book.title}</h3>
                        <p className="text-sm text-muted-foreground">{book.author}</p>
                        <div className="flex justify-between items-center mt-2">
                          <Badge variant="outline">{book.category_name || 'General'}</Badge>
                          <span className="text-sm text-green-600">{book.available_copies} available</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="return">
            <Card>
              <CardHeader>
                <CardTitle>Books to Return</CardTitle>
                <CardDescription>Currently issued books</CardDescription>
              </CardHeader>
              <CardContent>
                {issues.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No books currently issued</p>
                ) : (
                  <div className="space-y-3">
                    {issues.map((issue, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{issue.book_title || 'Book'}</p>
                          <p className="text-sm text-muted-foreground">
                            Issued to: {issue.student_name || 'Student'} | Due: {issue.due_date}
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => handleReturnBook(issue.id)}>
                          <ArrowRightLeft className="h-4 w-4 mr-1" /> Return
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Issue Book Dialog */}
        <Dialog open={issueDialogOpen} onOpenChange={setIssueDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Issue Book</DialogTitle>
              <DialogDescription>Issue "{selectedBook?.title}" to a student</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Select Student *</Label>
                <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a student" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((student) => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.name} ({student.roll_number || student.student_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-sm text-muted-foreground">
                Due date will be set to 14 days from today.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIssueDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleIssueBook}>Issue Book</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LibraryPageWrapper;
